import { NO_LOADING_DATE_KEY } from 'src/components/dispatch/unassignedDisplay/loadingDateGroups';
import { SameLocationInfo, SameLocationTarget } from 'src/components/dispatch/unassignedDisplay/sameLocationVM';
import { DispatchCard, UnassignedItem } from 'src/entities/dispatch';
import { DeliveryListGroupingValues } from 'src/entities/licenseEntity';
import { businessDayOf } from 'src/pages/LongHaul/buildDropTargets';

import { buildDisplayRotationNumbers, VehicleRowGroup } from './adapter';
import { estimateHubPlaceKeys, isDispatchedCard } from './estimateHubPlaceKeys';
import { isMainWarehouseSide, PlaceSide, placeKeyOf, placeKeysOf } from './placeKeys';

/**
 * 未割当案件ごとに「同じ地点がすでに他車両へ割当済みか」を判定する
 * (logposeTech/log_web_issues#318)。
 *
 * 地点の同一性は `placeKeys.ts` の `placeKeyOf` だけで決める。配車済みカードは
 * `DispatchCard.placeKey` に同じ関数の結果を持っているので、こちらは未割当案件側の
 * キーを同じ関数で作って突き合わせるだけにする。
 *
 * 突合は積み降ろしを問わない (2026-09-03 の拡張)。未割当案件の積地・降地の双方を、
 * 配車済みカードの積地・降地の双方と突き合わせる (クロス含む 4 方向)。同じ建物で
 * 集荷と配達が分かれている地点を拾うため。`placeKeyOf` は side をキーに含めないので、
 * 同一座標なら積地キーと降地キーが同じ文字列になり、キーの変更なしにクロスで一致する。
 *
 * 拠点 (配送センター・共同物流倉庫) は両側で判定から外す。積みも降ろしも大量に発生し、
 * 警告がノイズになる (dev 実測でタグ 37 件のうち 35 件が 1 拠点由来)。拠点かどうかは
 * 主要倉庫フラグ (places.is_main_warehouse) と当日データからの推定
 * (`estimateHubPlaceKeys`) の **OR** で決める。理由はそちらのファイル冒頭に書いた。
 *
 * Why not 既存バッジ (duplicateStoreNames) と同じ storeName 一致:
 * 同名で住所の違う拠点が合流するうえ、実データでは逆に「同一座標だが名称が違う」
 * ケースが多く取りこぼす (2025-07-07 の実測で未割当 1342 件のうち placeKey 判定 112 件に対し
 * storeName 判定は 29 件。差の 83 件はすべて座標一致・名称違い)。
 *
 * Why not 判定を配車済みカードどうしにも広げる: 既存の「他車両での取扱あり」バッジと
 * 詳細パネルの警告は storeName 判定のまま据え置く (#318 のスコープ外)。
 *
 * `driverNameById` はツールチップの 2 行目に出す乗務者名の解決用 (log_web_issues#323)。
 * 行の `driverId` から引くので車両単位ではなくここで受ける。省略すると車両の代表 1 人
 * (`Vehicle.driverName`) に落ちる。
 *
 * `scope` は突合を同じ積日どうしに限るかどうか (log_web_issues#338、ユーザー指摘 2026-09-29)。
 * 既定の 'all' は日を見ない。1 日を開く `/map`・カード配車では画面に出ているものが
 * そもそも同じ日なので、日を見なくても結果が変わらない。複数日を 1 画面に出す長距離配車
 * だけ 'loadingDate' を渡す。9/2 に割り当てただけの地点が 9/8 の未割当案件にも
 * 「同一地点で割当済」と出ていた。
 */
export function buildSameLocationAssignments(
  items: readonly UnassignedItem[],
  rowGroups: readonly VehicleRowGroup[],
  grouping: DeliveryListGroupingValues,
  driverNameById?: ReadonlyMap<number, string>,
  scope: SameLocationScope = 'all',
): Map<string, SameLocationInfo> {
  const result = new Map<string, SameLocationInfo>();
  if (items.length === 0 || rowGroups.length === 0) return result;

  const hubPlaceKeys = estimateHubPlaceKeys(items, rowGroups, grouping);
  const assigned = collectAssignedTargets(rowGroups, hubPlaceKeys, driverNameById, scope);
  if (assigned.size === 0) return result;

  for (const item of items) {
    const targets = collectTargetsForItem(item, assigned, grouping, hubPlaceKeys, scope);
    if (targets.length > 0) result.set(item.id, { targets });
  }

  return result;
}

/**
 * 突き合わせる側。降地を先に見るのは、#318 当初から降地だけで出していた並び順を保つため
 * (ツールチップの行順が変わると「増えた」以上の差分に見える)。
 */
const JUDGED_SIDES: readonly PlaceSide[] = ['unloading', 'loading'];

/** 'all' = 日を見ない（既定） / 'loadingDate' = 同じ積日どうしだけ突き合わせる */
export type SameLocationScope = 'all' | 'loadingDate';

/**
 * 索引のキー。積日でしぼるときだけ日を前に付ける。
 *
 * 積日が引けないものは `NO_LOADING_DATE_KEY` で 1 つの束にする。積地の時刻を持たない
 * 案件どうしは日で分けようがないので、日を見る前と同じ扱いにする。
 *
 * Why not 積日が無いものを一致させない: 時刻が入っていない会社ではタグが丸ごと
 * 出なくなる。「どちらにも日が無い」ことは「違う日」の根拠にならない。
 */
const indexKeyOf = (placeKey: string, loadingDate: string | null, scope: SameLocationScope): string => (
  scope === 'loadingDate' ? `${loadingDate || NO_LOADING_DATE_KEY}::${placeKey}` : placeKey
);

/**
 * 割当済みカードの積日。カードが束ねている legRun の積地開始時刻（業務日）を全部返す。
 *
 * 未割当側の `UnassignedItem.loadingDate`（`adapter.ts` の `toUnassignedItem`）と
 * 同じ導出にする。片方だけ別の規則で切ると、見出しの積日グループと突合の日が食い違う。
 *
 * 1 枚のカードが複数日の legRun を束ねることがある（同じ地点へ 2 日ぶん積む）ので、
 * 代表 1 日に丸めず全部の日で索引に載せる。
 */
function loadingDatesOf(card: DispatchCard, loadingDateByLegRunId: ReadonlyMap<number, string>): string[] {
  const dates = new Set<string>();
  for (const legRunId of card.legRunIds ?? []) {
    dates.add(loadingDateByLegRunId.get(legRunId) ?? NO_LOADING_DATE_KEY);
  }
  return dates.size > 0 ? [...dates] : [NO_LOADING_DATE_KEY];
}

/** その行の legRun ID → 積日。カードは legRunIds しか持たないので行の生データから引く */
function buildLoadingDateByLegRunId(row: { source?: VehicleRowGroup['rows'][number]['source'] }): Map<number, string> {
  const byId = new Map<number, string>();
  for (const vmCard of row.source?.cards ?? []) {
    for (const legRun of vmCard.legRuns ?? []) {
      if (legRun.id == null || !legRun.loading_start_at) continue;
      const day = businessDayOf(legRun.loading_start_at);
      if (day) byId.set(legRun.id, day);
    }
  }
  return byId;
}

/**
 * 1 案件ぶんの相手を集める。積地・降地の両側で突き合わせて和集合を取り、
 * 同じ相手を 2 回数えないよう `dedupeKeyOf` で重複排除する
 * (積地と降地が同座標の legRun では 2 本のキーが同じ文字列になる)。
 */
function collectTargetsForItem(
  item: UnassignedItem,
  assigned: Map<string, SameLocationTarget[]>,
  grouping: DeliveryListGroupingValues,
  hubPlaceKeys: ReadonlySet<string>,
  scope: SameLocationScope,
): SameLocationTarget[] {
  const targets: SameLocationTarget[] = [];
  const seen = new Set<string>();

  for (const side of JUDGED_SIDES) {
    // fallback に item.id を渡すのは、座標も名称も無い legRun を誰とも一致させないため。
    // カード側の fallback は `op-{operationId}` なので、どちらの一意値とも衝突しない
    const placeKey = placeKeyOf(side, item.source, item.id, grouping);

    // 拠点は判定から外す。マスタのフラグと当日データからの推定の OR。
    // どちらでも拠点でなければ外さない (タグが増える向きに倒す)
    if (isMainWarehouseSide(side, item.source) || hubPlaceKeys.has(placeKey)) continue;

    const found = assigned.get(indexKeyOf(placeKey, item.loadingDate ?? null, scope));
    if (!found) continue;

    for (const target of found) {
      const key = dedupeKeyOf(target);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push(target);
    }
  }

  return targets;
}

/**
 * レーン (地点単位表示) 1 行ぶんの情報にまとめる。
 *
 * レーンは同一地点の伝票をまとめたグループなので、配下 5 伝票が同じ 1 台を指す。
 * 素朴に連結すると「割当済 (5)」になり、読み手が台数と取り違える。
 * 重複排除の単位は伝票カードと同じ 行 (車両 × 回転) × 地点名。
 *
 * Why not サンプルのように先頭 1 件だけ拾う: レーンキーは積地→降地のペアなので
 * 降地は同一だが、配下の伝票ごとに割当済みの相手が違いうる (座標が同じで名称の違う
 * 地点が実データに存在する)。先頭だけだと他の相手が落ちる。
 */
export function mergeSameLocationInfos(
  infos: readonly (SameLocationInfo | undefined)[],
): SameLocationInfo | undefined {
  const targets: SameLocationTarget[] = [];
  const seen = new Set<string>();

  for (const info of infos) {
    if (!info) continue;
    for (const target of info.targets) {
      const key = dedupeKeyOf(target);
      if (seen.has(key)) continue;
      seen.add(key);
      targets.push(target);
    }
  }

  return targets.length > 0 ? { targets } : undefined;
}

/**
 * 重複排除の単位。行 (= 車両 × 回転) と地点名で決める。
 * 地点名まで見るのは、同じ行が同一座標の別名称カードを 2 枚持つとき
 * （実データにある）に両方の名前を見せたいため。
 */
const dedupeKeyOf = (t: SameLocationTarget): string => `${t.rowId}-${t.storeName}`;

/** ツールチップに出す地点名。積地カードは storeName を持たないので locationName を読む */
const storeNameOf = (card: DispatchCard): string => (
  card.type === 'delivery' ? card.storeName : card.locationName
) ?? '';

/** placeKey → 割当済みの訪問。行 × 地点名で重複排除する */
function collectAssignedTargets(
  rowGroups: readonly VehicleRowGroup[],
  hubPlaceKeys: ReadonlySet<string>,
  driverNameById: ReadonlyMap<number, string> | undefined,
  scope: SameLocationScope,
): Map<string, SameLocationTarget[]> {
  const byPlaceKey = new Map<string, SameLocationTarget[]>();
  const seen = new Map<string, Set<string>>();

  for (const group of rowGroups) {
    // 回転番号は車両リストのバッジと同じ導出にする。生の rotationNumber だと
    // 回転追加の仮想行と FR placeholder の非表示でバッジとずれる
    const rotationNumbers = buildDisplayRotationNumbers(group.rows);

    group.rows.forEach((row, index) => {
      // 行ごとに引き直す。legRun は行（勤務）の中にしか無い
      const loadingDateByLegRunId = scope === 'loadingDate'
        ? buildLoadingDateByLegRunId(row)
        : new Map<number, string>();

      for (const card of row.cards) {
        // 固定ルート由来の仮カードは割当済みに数えない (判定理由は isDispatchedCard)。
        // LoadingCard は status を持たないが、積地カードは実 operation 由来しか無いので
        // 取りこぼしは起きない (固定ルートの stop は template / no_delivery になり
        // buildDeliveryCard へ回る)
        if (!isDispatchedCard(card)) continue;
        if (!card.placeKey) continue;
        // 拠点は判定から外す (積地カード・降地カードの両方)。
        //
        // Why 推定もここに書く: 推定は placeKey だけで決まるので、未割当側で同じキーを
        // 外していれば結果は変わらない (引く前に continue するため)。それでも両側に
        // 置くのは、フラグの側が非対称だから — フラグは未割当側が legRun 単位、カード側は
        // マージされた legRun の OR で、カード側だけが拾える拠点がある。片側だけ推定を
        // 書くと「なぜこちらだけ」の説明が要る非対称がもう 1 つ増える
        // 代表キーが拠点ならカードごと外す（#322 前と同じ判定を弱めない）
        if (card.isMainWarehouse || hubPlaceKeys.has(card.placeKey)) continue;

        const target: SameLocationTarget = {
          rowId: row.rowId,
          vehicleId: group.vehicle.id,
          vehicleNumber: group.vehicle.vehicleNumber,
          rotationNumber: rotationNumbers[index],
          storeName: storeNameOf(card),
          // 乗務者名は行 (= 勤務) の driverId から引く。Vehicle.driverName は truck 単位の
          // 代表 1 人なので、1 台に 2 人が同じ時間帯で乗る勤務では 2 件目以降が別人の
          // 名前になる (buildMoveTargetRows と同じ解決)
          driverName: (row.driverId != null ? driverNameById?.get(row.driverId) : undefined)
            ?? group.vehicle.driverName,
          // 実体は委託先名なので自社車両では空文字。空のときに区切りごと落とすのは描画側
          companyName: group.vehicle.companyName,
        };
        const dedupeKey = dedupeKeyOf(target);
        const loadingDates = scope === 'loadingDate'
          ? loadingDatesOf(card, loadingDateByLegRunId)
          : [null];

        // 代表座標キーへ寄せられて消えた元のキーでも引けるようにする
        // (log_web_issues#322)。カードは 1 枚 = placeKey 1 本なので、これが無いと
        // 「吸収された側の座標と一致する未割当案件」に警告が出ない
        for (const placeKey of placeKeysOf(card)) {
          // 別名にも拠点判定を掛ける。未割当側 (collectTargetsForItem) が同じキーを
          // 外すので結果は変わらないが、上の代表キーの判定と非対称にしない
          // （主要倉庫フラグが未割当側 legRun 単位・カード側 OR で既に非対称なので、
          //   非対称をもう 1 つ増やさない）
          if (hubPlaceKeys.has(placeKey)) continue;

          for (const loadingDate of loadingDates) {
            const indexKey = indexKeyOf(placeKey, loadingDate, scope);
            const seenForPlace = seen.get(indexKey) ?? new Set<string>();
            if (seenForPlace.has(dedupeKey)) continue;
            seenForPlace.add(dedupeKey);
            seen.set(indexKey, seenForPlace);

            const bucket = byPlaceKey.get(indexKey);
            if (bucket) bucket.push(target);
            else byPlaceKey.set(indexKey, [target]);
          }
        }
      }
    });
  }

  return byPlaceKey;
}
